import { useEffect, useState } from 'react';

import { fetchAddress } from '../services/location';
import { store } from '../store';
/**
 * Location Fetching Module
 * * function to check for native location and GPS permission before invoking fetching hook
 * * Hooks has navigator success and failure callback to pass into invoking component. And click handler if location is to be fetched on click
 * * Wrapper on hook make it usable in class based component - where location needs to be fetched on mount
 */

export function getNativeGeolocation(locationFunction) {
  // - SDK - check for native location and GPS permission before invoking fetching location- invokes fetch location if permission are present, else prompts user to allow permission
  // - Browser - prompts user if asking has not been denied, else invokes fetching location
  window.checkTheGpsPermission = (value) => {
    if (value && locationFunction) {
      locationFunction();
    }
  };

  if (window.Android?.getLocationPermissions) {
    // Android SDK
    return window.Android.getLocationPermissions();
  } else if (window.ReactNativeWebView) {
    // React Native SDK
    window.ReactNativeWebView.postMessage(
      JSON.stringify({
        method: 'GET_LOCATION_PERMISSIONS',
      })
    );
  } else if (window.flutter_inappwebview) {
    // Flutter SDK
    window.flutter_inappwebview.callHandler(
      'FlutterWebView',
      JSON.stringify({ name: 'GET_LOCATION_PERMISSIONS' })
    );
  } else {
    // Browser
    window.checkTheGpsPermission(true);
  }
}

/**
 * Module to pass lat, long to the components
 * @param {needCity} - invoke API call to get city, pincode and formatted address
 */
export const useFetchLocation = (props) => {
  const [loading, setLoading] = useState(false);
  const [location, setLocation] = useState({
    lat: -1,
    long: -1,
    street: '',
    city: '',
    pincode: '',
    state: '',
    formattedAddress: '',
  });
  const [failed, setFailed] = useState(false);

  const options = {
    timeout: 15000, // 15s before failure
  };
  const { authToken } = store.getState().user;
  /**
   * If permission granted to navigator from browser or sdk
   */
  const successCallback = async (pos) => {
    const { latitude, longitude } = pos.coords;
    try {
      if (props.needCity) {
        const res = await fetchAddress(latitude, longitude, authToken);
        const { street, city, postalCode, state, formattedAddress } =
          res.result;
        setLocation((prev) => ({
          lat: latitude,
          long: longitude,
          street,
          city,
          pincode: postalCode,
          state,
          formattedAddress,
        }));
      } else {
        setLocation((prev) => ({
          lat: latitude,
          long: longitude,
        }));
      }
    } catch (err) {
      console.error('Failed to fetch location', err);
    } finally {
      setLoading(false);
    }
  };
  /**
   * If permissions denied
   */
  const failureCallback = (err) => {
    setFailed(true);
    setLoading(false);
  };

  const locationClickHandler = (enableHighAccuracy = false) => {
    if (loading) return; // only invokes once before loading becomes false again
    setLoading(true);

    getNativeGeolocation(
      navigator.geolocation.getCurrentPosition(
        successCallback,
        failureCallback,
        { ...options, enableHighAccuracy }
      )
    );
  };

  return { loading, location, failed, locationClickHandler };
};

/**
 * Wrapper on top of useFetchLocation hook to invoke it in a component because hooks cannot be directly invoked in class based components. Primarily where location has to be fetched on mount
 *
 * Usage: Place it in render JSX with condition that currentLocation is empty
 *
 * On Mount, It invokes hook, which check sdk permission, and invokes api call if city needed - and updates local state - then gets removed from class JSX due to conditional rendering
 */
export const LocationFetcherWrapper = ({ onFetch, needCity }) => {
  const data = useFetchLocation({ needCity });

  useEffect(() => {
    // trigger hook on mount
    data.locationClickHandler();
  }, []);

  useEffect(() => {
    onFetch(data); // Pass data into function passed from screen component
  }, [data.loading, data.failed]);

  return <></>;
};
